import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type Dispatch, type SetStateAction } from "react";
import { mapHighestLevelToCategory } from "../../../Utils/helperFunctions";
import { resolveCategoryWithFallback, type AuctionDivision } from "../../../Utils/auctionRules";
import { compactCurrency, getPlayerPhoto, getRoleIcon } from "./helpers";

type SortConfig = {
  key: string;
  direction: "asc" | "desc";
} | null;

type PlayersTableProps = {
  sortedPlayers: any[];
  setSortConfig: Dispatch<SetStateAction<SortConfig>>;
  activeDivision: AuctionDivision;
};

export const PlayersTable = ({
  sortedPlayers,
  setSortConfig,
  activeDivision,
}: PlayersTableProps) => {
  return (
    <TableContainer
      component={Paper}
      sx={{
        mt: 0,
        background: "linear-gradient(180deg, #2a2a2a 0%, #212121 100%)",
        borderRadius: { xs: "1.1vw", md: "0.45vw" },
        overflow: "visible",
        height: "auto",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        borderBottom: { xs: "0.14vw solid rgba(227,111,23,0.55)", md: "0.06vw solid rgba(227,111,23,0.55)" },
        borderRight: { xs: "0.14vw solid #c7b500", md: "0.06vw solid #c7b500" },
        borderLeft: { xs: "0.14vw solid rgba(255,205,0,0.2)", md: "0.06vw solid rgba(255,205,0,0.2)" },
        boxShadow: { xs: "inset -0.14vw 0 0 #c7b500", md: "inset -0.06vw 0 0 #c7b500" },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Table sx={{ minWidth: 0, tableLayout: "fixed", width: "100%" }}>
        <colgroup>
          <col style={{ width: "37%" }} />
          <col style={{ width: "23%" }} />
          <col style={{ width: "22%" }} />
          <col style={{ width: "18%" }} />
        </colgroup>
        <TableHead>
          <TableRow>
            {[
              { label: "Name of the player", sortKey: "name" },
              { label: "Category" },
              { label: "Role" },
              { label: "Price" },
            ].map(({ label, sortKey }) => (
              <TableCell
                key={label}
                onClick={() => {
                  if (!sortKey) return;
                  setSortConfig((prev) =>
                    prev?.key === sortKey
                      ? {
                          key: sortKey,
                          direction: prev.direction === "asc" ? "desc" : "asc",
                        }
                      : { key: sortKey, direction: "asc" },
                  );
                }}
                sx={{
                  color: "#fff",
                  background: "#1c2f72",
                  position: "sticky",
                  top: 0,
                  zIndex: 3,
                  border: "none",
                  py: { xs: "1.45vw", md: "0.7vw" },
                  px: { xs: "1.35vw", md: "0.95vw" },
                  fontSize: { xs: "2.6vw", md: "0.82vw" },
                  fontWeight: 800,
                  cursor: sortKey ? "pointer" : "default",
                  whiteSpace: "nowrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.45vw", md: "0.95vw" } }}>
                  {label}
                  {sortKey && (
                    <SwapVertIcon sx={{ fontSize: { xs: "2.6vw", md: "0.9vw" }, opacity: 0.9 }} />
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPlayers.length > 0 ? (
            sortedPlayers.map((player: any, index: number) => {
              const role = player?.Primary_Role__c || "-";
              return (
                <TableRow
                  key={player?.Id || index}
                  sx={{ borderBottom: { xs: "0.14vw solid rgba(227,111,23,0.44)", md: "0.06vw solid rgba(227,111,23,0.44)" } }}
                >
                  <TableCell
                    sx={{
                      color: "#fff",
                      border: "none",
                      py: { xs: "0.85vw", md: "0.5vw" },
                      px: { xs: "1.35vw", md: "0.95vw" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.9vw", md: "0.5vw" } }}>
                      <Box
                        component="img"
                        src={getPlayerPhoto(player)}
                        alt={player?.Player__r?.Name || "Player"}
                        sx={{
                          width: { xs: "5vw", md: "1.75vw" },
                          height: { xs: "5vw", md: "1.75vw" },
                          borderRadius: "50%",
                          objectFit: "cover",
                          minWidth: { xs: "5vw", md: "1.75vw" },
                          minHeight: { xs: "5vw", md: "1.75vw" },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: { xs: "2.45vw", md: "0.72vw" },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {player?.Player__r?.Name || "-"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      border: "none",
                      fontSize: { xs: "2.45vw", md: "0.72vw" },
                      px: { xs: "1.35vw", md: "0.95vw" },
                      py: { xs: "0.85vw", md: "0.5vw" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {resolveCategoryWithFallback({
                      division: activeDivision,
                      basePrice:
                        player?.Base_Price__c ??
                        player?.Reserve_Bid_Amount__c ??
                        player?.basePrice,
                      mcaNo:
                        player?.MCANo ??
                        player?.MCA_Registration_Number__c ??
                        player?.MCA_No__c ??
                        player?.Player__r?.MCA_Registration_Number__c ??
                        player?.Player__r?.MCA_No__c,
                      playerName: player?.Player__r?.Name || player?.name,
                      fallbackCategory: mapHighestLevelToCategory(
                        player?.Recent_Competitive_Level__c,
                      ),
                      playerAge:
                        player?.Player_Age__c ?? player?.Player__r?.Player_Age__c,
                      birthData:
                        player?.Birth_Date__c ??
                        player?.Date_of_Birth__c ??
                        player?.Player__r?.Birth_Date__c ??
                        player?.Player__r?.Date_of_Birth__c,
                    })}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      border: "none",
                      fontSize: { xs: "2.45vw", md: "0.72vw" },
                      px: { xs: "1.35vw", md: "0.95vw" },
                      py: { xs: "0.85vw", md: "0.5vw" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.55vw", md: "0.42vw" }, whiteSpace: "nowrap" }}>
                      <Box
                        component="img"
                        src={getRoleIcon(role)}
                        alt=""
                        sx={{ width: { xs: "2.55vw", md: "0.82vw" }, height: { xs: "2.55vw", md: "0.82vw" }, objectFit: "contain" }}
                      />
                      {role}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      border: "none",
                      fontWeight: 800,
                      fontSize: { xs: "2.45vw", md: "0.72vw" },
                      px: { xs: "1.35vw", md: "0.95vw" },
                      py: { xs: "0.85vw", md: "0.5vw" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {player?.Winning_Bid__c != null
                      ? compactCurrency(player.Winning_Bid__c)
                      : "--"}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={4}
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  border: "none",
                  textAlign: "center",
                  py: { xs: "5vw", md: "4vw" },
                  fontSize: { xs: "2.6vw", md: "0.82vw" },
                }}
              >
                <SportsBaseballIcon sx={{ mr: { xs: "0.8vw", md: "0.5vw" }, fontSize: { xs: "2.8vw", md: "1vw" }, verticalAlign: "middle" }} />
                No players available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
